import React, { useRef, useState } from 'react'
import cn from 'classnames'
import { ArrowBigUp } from 'lucide-react'
import styles from 'components/modals/freeUpvalueIntro.module.scss'
import { SignalButton } from 'components/votes/buttons/SignalButton'
import useStore from 'state/knovStore'
import api from 'api/api'
import { SATOSHI_UNIT, USD_UNIT, DEFAULT_UPVALUE_SATS } from 'lib/bsv-util'
import { Answer } from 'types/answers'

// A throwaway answer just so the demo buttons render; they are non-interactive.
const DEMO_ANSWER = {
    id: 'free-upvalue-intro-demo',
    user_id: 'free-upvalue-intro-demo-author',
    bsv_lock_amt: 0,
} as unknown as Answer

const noop = () => {}

// Renders the real upvalue button in either its free (first press) or paid
// (default upvalue amount) state. Wrapped so it's display-only. The paid variant
// gently pulses its amount to draw the eye to the difference between the two.
function DemoButton({ nextPressIsFree }: { nextPressIsFree: boolean }) {
    const quickLockRef = useRef<HTMLDivElement>(null)
    const bsvUnit = useStore(state => state.bsvUnit)
    const upvalueSats = useStore(state => state.upvalueSats)
    const upvalueUsd = useStore(state => state.upvalueUsd)

    return (
        <div className={cn(styles.demoButton, !nextPressIsFree && styles.paidDemo)} aria-hidden>
            <SignalButton
                signalType="upvalue"
                showBoost={false}
                useSats={bsvUnit === SATOSHI_UNIT}
                useUsd={bsvUnit === USD_UNIT}
                quickLockRef={quickLockRef}
                upvalueSats={upvalueSats ?? DEFAULT_UPVALUE_SATS}
                upvalueUsd={upvalueUsd}
                handleLockOrUpvalue={noop}
                animateLock={noop}
                currentUser={gon?.currentUser}
                answer={DEMO_ANSWER}
                nextPressIsFree={nextPressIsFree}
                upvaluesEnabled={true}
                freeReactionAvailable={nextPressIsFree}
            />
        </div>
    )
}

interface FreeUpvalueIntroProps {
    closeModal: () => void
}

export default function FreeUpvalueIntro({ closeModal }: FreeUpvalueIntroProps) {
    const [dontShowAgain, setDontShowAgain] = useState(true)

    const handleGotIt = () => {
        // Only remember the intro as seen when the user opts in; otherwise it
        // will show again on their next free upvalue.
        try {
            if (dontShowAgain && gon?.currentUser) {
                gon.currentUser.options = {
                    ...(gon.currentUser.options || {}),
                    has_seen_upvalue_intro: true,
                }
                api.updateUserOptions({ has_seen_upvalue_intro: true })?.catch((error: unknown) => {
                    if (gon?.currentUser?.options) {
                        gon.currentUser.options.has_seen_upvalue_intro = false
                    }
                    console.error('Failed to persist upvalue intro flag', error)
                })
            }
        } catch (error) {
            console.error('Failed to persist upvalue intro flag', error)
        }
        closeModal()
    }

    return (
        <div className={styles.comp} data-testid="free-upvalue-intro-modal">
            <div className={styles.header}>
                <ArrowBigUp
                    size={20}
                    color="#22c55e"
                    fill="#22c55e"
                    className={styles.headerIcon}
                />
                <span className={styles.headerTitle}>How upvalues work</span>
            </div>
            <p className={styles.text}>
                Treechat reactions are called upvalue. The first upvalue for a message is free. Free
                upvalues look like this:
            </p>
            <div className={styles.demoRow}>
                <DemoButton nextPressIsFree={true} />
            </div>
            <p className={styles.text}>
                All upvalues after that send a micropayment from your wallet to the message author.
                The amount you send by default is shown on the upvalue button:
            </p>
            <div className={styles.demoRow}>
                <DemoButton nextPressIsFree={false} />
            </div>
            <div className={styles.footer}>
                <label className={styles.dontShow}>
                    <input
                        type="checkbox"
                        data-testid="free-upvalue-intro-dont-show-checkbox"
                        checked={dontShowAgain}
                        onChange={ev => setDontShowAgain(ev.target.checked)}
                    />
                    <span>Don&apos;t show this again</span>
                </label>
                <button
                    type="button"
                    className={styles.gotItButton}
                    data-testid="free-upvalue-intro-got-it-button"
                    onClick={handleGotIt}
                >
                    Got it
                </button>
            </div>
        </div>
    )
}
